<script lang="ts">
  import {
    bizReviewStore,
    getBizReviews,
  } from 'src/stores/biz-reviews/biz-review-store';
  import type { FloatingWidgetPeriodicAutoShowConfig } from 'src/stores/floating-widget-settings-store';
  import {
    floatingWidgetSettingsStore,
    getFloatingWidgetSettings,
  } from 'src/stores/floating-widget-settings-store';
  import { setTempStopTracking, trackWidgetLoad } from 'src/utils/telemetry';
  import { getTextColorFromBGHex } from 'src/utils/theme-text';
  import { quintInOut } from 'svelte/easing';
  import MainButton from './main-button/MainButton.svelte';
  import SmallCard from './small-card/SmallCard.svelte';

  export let businessKey: string;

  const cardTransition = (_node, { duration, delay }) => {
    return {
      css: (t) => {
        return `
          transform: translateY(${(1 - t) * 20}px);
          opacity: ${t};
        `;
      },
      easing: quintInOut,
      duration,
      delay,
    };
  };

  let hasInteracted = false;
  /**
   * Handles first general interaction with the floating widget.
   */
  function handleInteract() {
    setTempStopTracking(false);
    hasInteracted = true;
  }

  let bizReviews = [];
  let loaded = false;
  let isFetchingReviews = false;

  bizReviewStore.subscribe((value) => {
    bizReviews = value.displayData;

    isFetchingReviews = value.loading;
  });

  getBizReviews(businessKey);

  let smallCardViewIndex = 0;
  $: endOfSmallCardView = smallCardViewIndex >= bizReviews.length - 1;
  $: {
    if (loaded && smallCardViewIndex >= bizReviews.length - 4) {
      fetchMoreReviews();
    }
  }

  // ==== On Load ===
  function onLoad() {
    if (loaded) return;

    trackWidgetLoad('default', businessKey);
    loaded = true;
  }

  $: {
    if ($floatingWidgetSettingsStore.loaded && bizReviews.length) {
      onLoad();
    }
  }

  // ==== Mouse hover ===
  let smallCardHover = false;
  function handleSmallCardMouseEnter() {
    smallCardHover = true;
  }
  function handleSmallCardMouseLeave() {
    smallCardHover = false;
  }

  /**
   * Fetches more reviews.
   */
  function fetchMoreReviews() {
    getBizReviews(businessKey);
  }

  enum STATE {
    BUTTON = 'BUTTON',
    SMALL_CARD = 'SMALL_CARD',
  }

  const states = [STATE.BUTTON, STATE.SMALL_CARD];
  let stateIndex = 0;

  $: currState = states[stateIndex];
  $: showButtonClose = currState !== STATE.BUTTON;

  function handleMainButtonClick() {
    if (showButtonClose) {
      handleMinimize();
    } else {
      handleExpand();
    }
  }

  function handleExpand() {
    stateIndex = Math.min(stateIndex + 1, states.length - 1);
  }

  function handleMinimize() {
    stateIndex = Math.min(stateIndex - 1, states.length - 1);
  }

  // === Widget settings ===
  let themeColor = null;
  $: themeStyle =
    (themeColor &&
      `--primary: ${themeColor}; --theme-text-color: ${getTextColorFromBGHex(
        themeColor,
      )}`) ||
    '';
  getFloatingWidgetSettings(businessKey);

  // === Periodic Auto Show ===
  let periodicAutoShow: FloatingWidgetPeriodicAutoShowConfig = null;
  let periodicAutoShowTimeout = null;
  let autoShowNum = 0;
  $: periodicAutoShowMaxNum =
    periodicAutoShow.maxShowNum || Number.MAX_SAFE_INTEGER;
  $: canStillGoNext =
    currState !== STATE.BUTTON || // allow widget to close
    (!endOfSmallCardView && autoShowNum < periodicAutoShowMaxNum);
  $: periodicAutoShowAllowed =
    periodicAutoShow?.active &&
    loaded &&
    canStillGoNext &&
    !hasInteracted &&
    !smallCardHover &&
    states.includes(STATE.BUTTON) &&
    states.includes(STATE.SMALL_CARD);

  floatingWidgetSettingsStore.subscribe((value) => {
    themeColor = value.themeColor;
    periodicAutoShow = value.periodicAutoShow;
  });

  /**
   * Schedules an auto-show.
   */
  function scheduleAutoShow() {
    const shouldShow = !periodicAutoShowTimeout && currState === STATE.BUTTON;

    if (!shouldShow) {
      return;
    }

    periodicAutoShowTimeout = window.setTimeout(() => {
      periodicAutoShowTimeout = null;
      setTempStopTracking(true);
      handleExpand();

      if (autoShowNum > 0) {
        smallCardViewIndex = Math.min(
          smallCardViewIndex + 1,
          bizReviews.length - 1,
        );
      }
    }, periodicAutoShow.showAfterMs);
  }

  /**
   * Schedules an auto-next.
   */
  let smallCardRef: SmallCard;

  function scheduleAutoNext() {
    const shouldGoNext =
      !periodicAutoShowTimeout && currState == STATE.SMALL_CARD;

    if (!shouldGoNext) {
      return;
    }

    periodicAutoShowTimeout = window.setTimeout(() => {
      periodicAutoShowTimeout = null;
      setTempStopTracking(true);

      autoShowNum++;

      if (
        autoShowNum >= $floatingWidgetSettingsStore.periodicAutoShow.maxShowNum
      ) {
        handleMinimize();
        return;
      }

      scheduleAutoNext();
    }, periodicAutoShow.nextAfterMs);
  }

  $: {
    if (periodicAutoShowAllowed) {
      currState === STATE.BUTTON
        ? scheduleAutoShow()
        : currState === STATE.SMALL_CARD
        ? scheduleAutoNext()
        : void 0;
    } else {
      periodicAutoShowTimeout = window.clearTimeout(periodicAutoShowTimeout);
    }
  }
</script>

{#if loaded}
  <div
    class="widget"
    style={themeStyle}
    on:pointerdown={!hasInteracted && handleInteract}
  >
    <MainButton showClose={showButtonClose} on:click={handleMainButtonClick} />
  </div>

  {#if currState === STATE.SMALL_CARD}
    <div
      class="widget card rounded-borders"
      style={themeStyle}
      in:cardTransition={{ duration: 500, delay: 150 }}
      out:cardTransition={{ duration: 500, delay: 0 }}
      on:pointerdown={!hasInteracted && handleInteract}
      on:mouseenter={handleSmallCardMouseEnter}
      on:mouseleave={handleSmallCardMouseLeave}
    >
      <SmallCard
        bind:this={smallCardRef}
        reviews={bizReviews}
        {isFetchingReviews}
        bind:index={smallCardViewIndex}
        on:minimize={handleMinimize}
      />
    </div>
  {/if}
{/if}

<style lang="scss">
  $bottom: 20px;
  $buttonHeight: 50px;
  $cardBottomMargin: 12px;
  $cardBottom: $bottom + $buttonHeight + $cardBottomMargin;

  :global(.widget) {
    position: fixed !important;
    bottom: 20px;
    left: 20px;
    z-index: 10001;
  }

  .card {
    bottom: $cardBottom;
    box-shadow: 0 0 12px var(--primary);
  }
</style>
